/** Settings tabs: business details, verification + integrations + code injection, redirects (CSV import, 404 suggestions). */
import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Collapse, Form, Input, InputNumber, Modal, Row, Select, Space, Table, Tooltip, Typography, Upload, message } from 'antd';
import { CloseOutlined, DeleteOutlined, LockOutlined, PlusOutlined, SaveOutlined, UploadOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import { QUIET } from './api';
import { Hint, fmtDate, listOf, toastErr, useSite } from './common';
import { PlansModal, showPlans } from './Media';

const { Text } = Typography;

export const useSaveSite = () => {
    const { t } = useTranslation();
    const { api, setSite } = useSite();
    const [saving, setSaving] = useState(false);
    const save = async (body) => {
        setSaving(true);
        try {
            const s = await api.put('', body);
            setSite((old) => ({ ...old, ...s }));
            message.success(t('Saved'));
            return s;
        } catch (e) { toastErr(e); return null; } finally { setSaving(false); }
    };
    return { save, saving };
};

// ---- Business ----
const SOCIAL = [['facebook', 'Facebook'], ['instagram', 'Instagram'], ['x', 'X'], ['linkedin', 'LinkedIn'], ['youtube', 'YouTube'], ['tiktok', 'TikTok'], ['pinterest', 'Pinterest'], ['github', 'GitHub'], ['wechat', 'WeChat'], ['whatsapp', 'WhatsApp']];

/** name: path of the address object inside the form or the current Form.List row */
const AddressFields = ({ name }) => {
    const { t } = useTranslation();
    return (
        <Row gutter={12}>
            <Col span={24}><Form.Item name={[...name, 'street']} label={t('Street address')}><Input maxLength={200} /></Form.Item></Col>
            <Col xs={24} sm={12}><Form.Item name={[...name, 'city']} label={t('City')}><Input maxLength={100} /></Form.Item></Col>
            <Col xs={24} sm={12}><Form.Item name={[...name, 'region']} label={t('Region')}><Input maxLength={100} /></Form.Item></Col>
            <Col xs={24} sm={12}><Form.Item name={[...name, 'postal']} label={t('Postal code')}><Input maxLength={30} /></Form.Item></Col>
            <Col xs={24} sm={12}><Form.Item name={[...name, 'country']} label={t('Country')}><Input maxLength={80} /></Form.Item></Col>
        </Row>
    );
};

const HoursList = ({ name }) => {
    const { t } = useTranslation();
    return (
        <Form.Item label={t('Opening hours')} style={{ marginBottom: 16 }}>
            <Form.List name={name}>
                {(fields, { add, remove }) => (
                    <>
                        {fields.map((f) => (
                            <Space.Compact key={f.key} block style={{ marginBottom: 6 }}>
                                <Form.Item name={[f.name, 'days']} noStyle><Input placeholder="Mon-Fri" style={{ width: '40%' }} maxLength={40} /></Form.Item>
                                <Form.Item name={[f.name, 'open']} noStyle><Input type="time" style={{ width: '30%' }} aria-label={t('Opening time')} /></Form.Item>
                                <Form.Item name={[f.name, 'close']} noStyle><Input type="time" style={{ width: '30%' }} aria-label={t('Closing time')} /></Form.Item>
                                <Button icon={<DeleteOutlined />} danger onClick={() => remove(f.name)} />
                            </Space.Compact>
                        ))}
                        <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={() => add({ days: 'Mon-Fri', open: '09:00', close: '17:00' })}>{t('Add hours')}</Button>
                    </>
                )}
            </Form.List>
        </Form.Item>
    );
};

const cleanHours = (rows) => (rows || []).filter((h) => h?.days?.trim()).map((h) => ({ days: h.days.trim(), open: h.open || '', close: h.close || '' }));

export const BusinessTab = () => {
    const { t } = useTranslation();
    const { site } = useSite();
    const { save, saving } = useSaveSite();
    const [form] = Form.useForm();
    useEffect(() => {
        form.setFieldsValue({ name: '', legal_name: '', phone: '', email: '', address: {}, hours: [], locations: [], social: {}, ...(site.settings?.business || {}) });
    }, [site.settings?.business, form]);
    const submit = async () => {
        const v = await form.validateFields().catch(() => null);
        if (!v) return;
        const locations = (v.locations || []).map((l) => {
            const { lat, lng, ...rest } = l || {};
            return { ...rest, hours: cleanHours(l?.hours), ...(lat === null || lat === undefined ? {} : { lat }), ...(lng === null || lng === undefined ? {} : { lng }) };
        });
        save({ settings: { business: { ...v, hours: cleanHours(v.hours), locations } } });
    };
    return (
        <Form form={form} layout="vertical" requiredMark={false} style={{ maxWidth: 620 }}>
            <Row gutter={12}>
                <Col xs={24} sm={12}><Form.Item name="name" label={t('Business name')}><Input maxLength={120} /></Form.Item></Col>
                <Col xs={24} sm={12}><Form.Item name="legal_name" label={t('Legal name')}><Input maxLength={160} /></Form.Item></Col>
                <Col xs={24} sm={12}><Form.Item name="phone" label={t('Phone')}><Input maxLength={40} /></Form.Item></Col>
                <Col xs={24} sm={12}><Form.Item name="email" label={t('Email')} rules={[{ type: 'email' }]}><Input maxLength={200} /></Form.Item></Col>
            </Row>
            <AddressFields name={['address']} />
            <HoursList name="hours" />
            <Collapse ghost size="small" style={{ margin: '0 -16px 16px' }} items={[
                {
                    key: 'locations', forceRender: true, label: t('Locations'),
                    children: (
                        <Form.List name="locations">
                            {(fields, { add, remove }) => (
                                <>
                                    {fields.map((f) => (
                                        <div key={f.key} style={{ border: '1px solid var(--ant-color-border-secondary, #eee)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                                            <Row gutter={12} align="top">
                                                <Col flex="auto"><Form.Item name={[f.name, 'name']} label={t('Name')}><Input maxLength={120} /></Form.Item></Col>
                                                <Col flex="none"><Button type="text" danger icon={<DeleteOutlined />} style={{ marginTop: 30 }} onClick={() => remove(f.name)} /></Col>
                                            </Row>
                                            <Form.Item name={[f.name, 'phone']} label={t('Phone')}><Input maxLength={40} /></Form.Item>
                                            <AddressFields name={[f.name, 'address']} />
                                            <HoursList name={[f.name, 'hours']} />
                                            <Space>
                                                <Form.Item name={[f.name, 'lat']} label={t('Latitude')}><InputNumber min={-90} max={90} style={{ width: 150 }} /></Form.Item>
                                                <Form.Item name={[f.name, 'lng']} label={t('Longitude')}><InputNumber min={-180} max={180} style={{ width: 150 }} /></Form.Item>
                                            </Space>
                                        </div>
                                    ))}
                                    <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ name: '', address: {}, hours: [] })}>{t('Add location')}</Button>
                                </>
                            )}
                        </Form.List>
                    ),
                },
                {
                    key: 'social', forceRender: true, label: t('Social links'),
                    children: (
                        <Row gutter={12}>
                            {SOCIAL.map(([k, label]) => <Col xs={24} sm={12} key={k}><Form.Item name={['social', k]} label={label} rules={[{ pattern: /^https?:\/\//i, message: 'https://…' }]}><Input placeholder="https://…" maxLength={300} /></Form.Item></Col>)}
                        </Row>
                    ),
                },
            ]} />
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={submit}>{t('Save')}</Button>
        </Form>
    );
};

// ---- Integrations ----
const VERIFICATION = [['google', 'Google'], ['bing', 'Bing'], ['baidu', 'Baidu'], ['yandex', 'Yandex'], ['pinterest', 'Pinterest'], ['facebook', 'Facebook']];
const INTEGRATIONS = [
    ['gtm', 'Google Tag Manager', 'GTM-XXXXXXX'], ['meta_pixel', 'Meta Pixel', '123456789012345'], ['linkedin', 'LinkedIn Insight', '1234567'],
    ['tiktok', 'TikTok Pixel', 'C1A2B3C4D5E6F7G8H9'], ['hotjar', 'Hotjar', '1234567'], ['clarity', 'Microsoft Clarity', 'abcd1234ef'],
    ['crisp', 'Crisp', 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'], ['tawk', 'Tawk.to', 'propertyId/widgetId'], ['intercom', 'Intercom', 'abc12345'],
];
const trimAll = (o) => Object.fromEntries(Object.entries(o || {}).map(([k, v]) => [k, String(v ?? '').trim()]));

export const IntegrationsTab = () => {
    const { t } = useTranslation();
    const { site } = useSite();
    const { save, saving } = useSaveSite();
    const [form] = Form.useForm();
    const canCode = !!site.plan?.custom_code;
    const s = site.settings || {};
    useEffect(() => {
        form.setFieldsValue({ verification: s.verification || {}, integrations: s.integrations || {}, custom_code: s.custom_code || {} });
    }, [s.verification, s.integrations, s.custom_code, form]); // eslint-disable-line react-hooks/exhaustive-deps
    const head = Form.useWatch(['custom_code', 'head'], form);
    const body = Form.useWatch(['custom_code', 'body'], form);
    const submit = async () => {
        const v = await form.validateFields().catch(() => null);
        if (!v) return;
        const settings = { verification: trimAll(v.verification), integrations: trimAll(v.integrations) };
        if (canCode) settings.custom_code = { head: v.custom_code?.head || '', body: v.custom_code?.body || '', css: v.custom_code?.css || '' };
        save({ settings });
    };
    const hasCode = !!(s.custom_code?.head || s.custom_code?.body || s.custom_code?.css);
    return (
        <Form form={form} layout="vertical" requiredMark={false} style={{ maxWidth: 620 }}>
            <Text strong style={{ display: 'block', marginBottom: 12 }}>{t('Site verification')}</Text>
            <Row gutter={12}>
                {VERIFICATION.map(([k, label]) => <Col xs={24} sm={12} key={k}><Form.Item name={['verification', k]} label={label}><Input maxLength={200} /></Form.Item></Col>)}
            </Row>
            <Text strong style={{ display: 'block', marginBottom: 12 }}>{t('Tracking and chat')}<Hint title={t('Trackers wait for cookie consent when the cookie banner is on')} /></Text>
            <Row gutter={12}>
                {INTEGRATIONS.map(([k, label, ph]) => <Col xs={24} sm={12} key={k}><Form.Item name={['integrations', k]} label={label}><Input placeholder={ph} maxLength={100} /></Form.Item></Col>)}
            </Row>
            <Collapse ghost size="small" style={{ margin: '0 -16px 16px' }} defaultActiveKey={hasCode ? ['code'] : []} items={[{
                key: 'code', forceRender: true,
                label: <Space size={6}>{!canCode && <LockOutlined />}{t('Code injection')}</Space>,
                children: (
                    <>
                        {!canCode && (
                            <Alert type="info" showIcon style={{ marginBottom: 12 }} message={t('Your plan does not include code injection')}
                                action={<Button size="small" type="primary" onClick={showPlans}>{t('Upgrade')}</Button>} />
                        )}
                        {(head || body) && <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={t('Custom code relaxes the page security policy for this site')} />}
                        <Form.Item name={['custom_code', 'head']} label={t('Before </head>')}><Input.TextArea disabled={!canCode} autoSize={{ minRows: 3, maxRows: 12 }} style={{ fontFamily: 'monospace' }} spellCheck={false} /></Form.Item>
                        <Form.Item name={['custom_code', 'body']} label={t('Before </body>')}><Input.TextArea disabled={!canCode} autoSize={{ minRows: 3, maxRows: 12 }} style={{ fontFamily: 'monospace' }} spellCheck={false} /></Form.Item>
                        <Form.Item name={['custom_code', 'css']} label="CSS"><Input.TextArea disabled={!canCode} autoSize={{ minRows: 3, maxRows: 12 }} style={{ fontFamily: 'monospace' }} spellCheck={false} /></Form.Item>
                    </>
                ),
            }]} />
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={submit}>{t('Save')}</Button>
            {!canCode && <PlansModal />}
        </Form>
    );
};

// ---- Redirects ----
const CODE_OPTIONS = (t) => [{ value: 301, label: `301 ${t('Permanent')}` }, { value: 302, label: `302 ${t('Temporary')}` }];
const keyed = (d) => listOf(d).map((x, i) => ({ ...x, _k: i }));

const ImportModal = ({ open, onClose, onImport }) => {
    const { t } = useTranslation();
    const [csv, setCsv] = useState('');
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    useEffect(() => { if (open) { setCsv(''); setResult(null); } }, [open]);
    const run = async () => {
        setBusy(true);
        try {
            const r = await onImport(csv);
            if (!r) return;
            if (r.errors?.length) { setResult(r); return; }
            message.success(t('{{added}} added, {{skipped}} skipped', { added: r.added || 0, skipped: r.skipped || 0 }));
            onClose();
        } finally { setBusy(false); }
    };
    return (
        <Modal open={open} title={t('Import CSV')} onCancel={onClose} onOk={run} okText={t('Import')} okButtonProps={{ disabled: !csv.trim() }} confirmLoading={busy} destroyOnHidden>
            <Input.TextArea value={csv} onChange={(e) => setCsv(e.target.value)} autoSize={{ minRows: 6, maxRows: 14 }} spellCheck={false} style={{ fontFamily: 'monospace', marginBottom: 8 }}
                placeholder={'/old-page,/new-page,301\n/blog/*,/news/:splat,301'} />
            <Upload accept=".csv,text/csv,text/plain" maxCount={1} showUploadList={false}
                beforeUpload={(f) => { f.text().then(setCsv).catch(toastErr); return false; }}>
                <Button size="small" icon={<UploadOutlined />}>{t('Choose file')}</Button>
            </Upload>
            {result && (
                <Alert type="warning" showIcon style={{ marginTop: 12 }} message={t('{{added}} added, {{skipped}} skipped', { added: result.added || 0, skipped: result.skipped || 0 })}
                    description={<div style={{ maxHeight: 160, overflow: 'auto' }}>{result.errors.map((e) => <div key={e.line}>{t('Line {{line}}: {{message}}', { line: e.line, message: e.message })}</div>)}</div>} />
            )}
        </Modal>
    );
};

export const RedirectsTab = () => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [items, setItems] = useState([]);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [importing, setImporting] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [draft, setDraft] = useState(null); // redirect being added from a suggestion
    const loadSuggestions = () => api.get('/redirects/suggestions', null, QUIET).then((d) => setSuggestions(listOf(d))).catch(() => {});
    useEffect(() => {
        api.get('/redirects').then((d) => setItems(keyed(d))).catch(toastErr).finally(() => setLoading(false));
        loadSuggestions();
    }, [api]); // eslint-disable-line react-hooks/exhaustive-deps
    const set = (k, patch) => { setDirty(true); setItems((xs) => xs.map((x) => (x._k === k ? { ...x, ...patch } : x))); };
    const persist = async (rows) => {
        setSaving(true);
        try {
            const r = await api.put('/redirects', { items: rows.filter((x) => x.from || x.to).map(({ from, to, code }) => ({ from, to, code })) });
            setItems(keyed(r));
            setDirty(false);
            message.success(t('Saved — publish to apply'));
            return true;
        } catch (e) { toastErr(e); return false; } finally { setSaving(false); }
    };
    // unsaved rows are saved first: the table reloads from the server after an import
    const importCsv = async (csv) => {
        if (dirty && !(await persist(items))) return null;
        try {
            const r = await api.post('/redirects/import', { csv });
            setItems(keyed(await api.get('/redirects')));
            return r;
        } catch (e) { toastErr(e); return null; }
    };
    const addDraft = async () => {
        if (!draft.to?.trim()) return;
        if (await persist([...items, { from: draft.from, to: draft.to.trim(), code: draft.code }])) {
            setSuggestions((xs) => xs.filter((x) => x.path !== draft.from));
            setDraft(null);
        }
    };
    const dismiss = async (path) => {
        try {
            await api.post('/redirects/suggestions/dismiss', { path });
            setSuggestions((xs) => xs.filter((x) => x.path !== path));
        } catch (e) { toastErr(e); }
    };
    return (
        <>
            <Table size="small" rowKey="_k" loading={loading} dataSource={items} pagination={items.length > 50 ? { pageSize: 50 } : false} scroll={{ x: 600 }}
                columns={[
                    { title: <>{t('From path')}<Hint title={t('End with /* to match everything under a path; use :splat in the target to keep the rest')} /></>, dataIndex: 'from', render: (v, r) => <Input size="small" value={v} placeholder="/old-page" onChange={(e) => set(r._k, { from: e.target.value })} /> },
                    { title: t('To'), dataIndex: 'to', render: (v, r) => <Input size="small" value={v} placeholder="/new-page" onChange={(e) => set(r._k, { to: e.target.value })} /> },
                    { title: t('Type'), dataIndex: 'code', width: 150, render: (v, r) => <Select size="small" value={v || 301} style={{ width: '100%' }} onChange={(code) => set(r._k, { code })} options={CODE_OPTIONS(t)} /> },
                    { title: '', key: 'd', width: 40, render: (_, r) => <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => { setDirty(true); setItems((xs) => xs.filter((x) => x._k !== r._k)); }} /> },
                ]} />
            <Space style={{ marginTop: 12 }} wrap>
                <Button icon={<PlusOutlined />} onClick={() => { setDirty(true); setItems((xs) => [...xs, { from: '', to: '', code: 301, _k: Date.now() }]); }}>{t('Add redirect')}</Button>
                <Button icon={<UploadOutlined />} onClick={() => setImporting(true)}>{t('Import CSV')}</Button>
                <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={() => persist(items)}>{t('Save')}</Button>
            </Space>
            {suggestions.length > 0 && (
                <div style={{ marginTop: 24 }}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>{t('Recent 404s')}</Text>
                    <Table size="small" rowKey="path" dataSource={suggestions} pagination={suggestions.length > 20 ? { pageSize: 20 } : false} scroll={{ x: 600 }}
                        columns={[
                            { title: t('Path'), dataIndex: 'path', ellipsis: true, render: (v, r) => (r.referrer ? <Tooltip title={r.referrer}><Text code>{v}</Text></Tooltip> : <Text code>{v}</Text>) },
                            { title: t('Hits'), dataIndex: 'hits', width: 80 },
                            { title: t('Last seen'), dataIndex: 'last_seen', width: 160, render: (v) => fmtDate(v) },
                            {
                                title: '', key: 'a', width: 170, align: 'right', render: (_, r) => (
                                    <Space size={0}>
                                        <Button size="small" type="link" onClick={() => setDraft({ from: r.path, to: r.suggested_to || '', code: 301 })}>{t('Add redirect')}</Button>
                                        <Tooltip title={t('Dismiss')}><Button size="small" type="text" icon={<CloseOutlined />} onClick={() => dismiss(r.path)} /></Tooltip>
                                    </Space>
                                ),
                            },
                        ]} />
                </div>
            )}
            <ImportModal open={importing} onClose={() => setImporting(false)} onImport={importCsv} />
            <Modal open={!!draft} title={t('Add redirect')} onCancel={() => setDraft(null)} onOk={addDraft} okText={t('Save')} confirmLoading={saving} okButtonProps={{ disabled: !draft?.to?.trim() }} destroyOnHidden>
                {draft && (
                    <Form layout="vertical">
                        <Form.Item label={t('From path')}><Input value={draft.from} onChange={(e) => setDraft({ ...draft, from: e.target.value })} /></Form.Item>
                        <Form.Item label={t('To')}><Input autoFocus value={draft.to} placeholder="/new-page" onChange={(e) => setDraft({ ...draft, to: e.target.value })} /></Form.Item>
                        <Form.Item label={t('Type')} style={{ marginBottom: 0 }}><Select value={draft.code} onChange={(code) => setDraft({ ...draft, code })} options={CODE_OPTIONS(t)} /></Form.Item>
                    </Form>
                )}
            </Modal>
        </>
    );
};
